/**
 * Achievement evaluator. Returns ONLY newly-unlocked entries — every
 * call diffs against `profile.achievements.unlocks`. Honors the
 * anti-trigger guards (cooldown, engagement minimum, master toggle).
 *
 * Achievements that need extra signals (bulk paste used, theme switches,
 * etc.) get their unlock recorded via dedicated store actions; this
 * evaluator only catches the data-derivable ones.
 */

import type {
  AchievementUnlock,
  AppData,
  Goal,
  Transaction,
} from '../../data/types';
import { canTriggerAchievements } from './guards';
import { ACHIEVEMENTS } from './registry';

const DAY_MS = 24 * 60 * 60 * 1000;

type Reason = (data: AppData, transactions: Transaction[], now: Date) => boolean;

/** Per-id "is this achievement met right now?" predicate. Anything
 *  not listed here is unlock-by-action only (e.g. bulk paste used). */
const REASONS: Record<string, Reason> = {
  // Logging
  log_3: (_, txs) => txs.length >= 3,
  log_25: (_, txs) => txs.length >= 25,
  log_100: (_, txs) => txs.length >= 100,
  log_500: (_, txs) => txs.length >= 500,
  first_receipt: (_, txs) => txs.some((t) => !!t.receiptId),
  first_preset: (data) => (data.profile.quickPresets ?? []).length >= 1,

  // Saving — total currentAmount across goals.
  savings_100: (data) => totalGoalSavings(data.goals) >= 100,
  savings_1k: (data) => totalGoalSavings(data.goals) >= 1000,
  savings_10k: (data) => totalGoalSavings(data.goals) >= 10000,
  first_goal_complete: (data) =>
    data.goals.some(
      (g) => g.targetAmount > 0 && g.currentAmount >= g.targetAmount,
    ),

  // Budgeting
  first_bill_scheduled: (data) =>
    data.scheduledItems.some((s) => s.type === 'expense'),
  net_worth_positive: (data) => {
    const last = (data.profile.netWorthHistory ?? []).slice(-1)[0];
    return !!last && last.total > 0;
  },
  net_worth_growth_30d: (data) => {
    const hist = data.profile.netWorthHistory ?? [];
    if (hist.length < 2) return false;
    const today = hist[hist.length - 1]!;
    const target = hist.find(
      (h) =>
        Date.parse(today.date) - Date.parse(h.date) >= 29 * DAY_MS,
    );
    return !!target && today.total > target.total;
  },

  // Streaks — read from achievements.streaks.
  streak_log_7: (data) =>
    (data.profile.achievements?.streaks.daysLogged.current ?? 0) >= 7,
  streak_log_30: (data) =>
    (data.profile.achievements?.streaks.daysLogged.current ?? 0) >= 30,
  streak_log_100: (data) =>
    (data.profile.achievements?.streaks.daysLogged.current ?? 0) >= 100,
  streak_under_pace_14: (data) =>
    (data.profile.achievements?.streaks.daysUnderPace.current ?? 0) >= 14,
  no_spend_3: (data) =>
    (data.profile.achievements?.streaks.noSpendDays.current ?? 0) >= 3,
  no_spend_7: (data) =>
    (data.profile.achievements?.streaks.noSpendDays.current ?? 0) >= 7,
  pyf_first: (data) =>
    (data.profile.achievements?.streaks.pyfStreak.current ?? 0) >= 1,
  pyf_3_periods: (data) =>
    (data.profile.achievements?.streaks.pyfStreak.current ?? 0) >= 3,
  pyf_12_periods: (data) =>
    (data.profile.achievements?.streaks.pyfStreak.current ?? 0) >= 12,

  // Milestones — install date based.
  first_month_complete: (data, _txs, now) =>
    daysSinceInstall(data, now) >= 30,
  six_months: (data, _txs, now) => daysSinceInstall(data, now) >= 180,
  one_year: (data, _txs, now) => daysSinceInstall(data, now) >= 365,
  five_year: (data, _txs, now) => daysSinceInstall(data, now) >= 365 * 5,
  first_pro_upgrade: (data) => !!data.profile.achievements && !!data.proPurchased,

  // Distinct-categories used in the most recent calendar period (rough
  // approximation: any 30-day window ending today).
  all_categories_used: (data, _txs, now) => {
    const start = now.getTime() - 30 * DAY_MS;
    const ids = new Set<string>();
    for (const t of data.transactions) {
      if (t.type !== 'expense') continue;
      if (Date.parse(t.date) >= start) ids.add(t.categoryId);
    }
    return ids.size >= 5;
  },
};

function totalGoalSavings(goals: Goal[]): number {
  return goals.reduce((s, g) => s + (g.currentAmount || 0), 0);
}

function daysSinceInstall(data: AppData, now: Date): number {
  const a = data.profile.achievements;
  if (!a) return 0;
  const installed = Date.parse(a.installedAt);
  if (!Number.isFinite(installed)) return 0;
  return Math.floor((now.getTime() - installed) / DAY_MS);
}

/**
 * Evaluate every registered achievement against current state. Returns
 * the list of NEW unlocks (the caller appends them to
 * profile.achievements.unlocks).
 */
export function evaluateAchievements(
  data: AppData,
  now: Date = new Date(),
): AchievementUnlock[] {
  const ach = data.profile.achievements;
  if (!ach) return [];
  if (!canTriggerAchievements(data.profile, data.transactions.length, now)) {
    return [];
  }
  const already = new Set(ach.unlocks.map((u) => u.id));
  const out: AchievementUnlock[] = [];
  for (const def of ACHIEVEMENTS) {
    if (already.has(def.id)) continue;
    const check = REASONS[def.id];
    if (!check) continue;
    if (check(data, data.transactions, now)) {
      out.push({
        id: def.id,
        unlockedAt: now.toISOString(),
        seen: false,
      });
    }
  }
  return out;
}
